import React, { useState, useEffect } from 'react';
import { FileText, Eye, Download, Printer, CheckCircle, Clock, PenTool, X, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { poaAPI } from '../utils/api';

// Signature font styles
const SIGNATURE_FONTS = {
  cursive: { fontFamily: "'Brush Script MT', cursive", fontSize: '28px' },
  elegant: { fontFamily: "'Times New Roman', serif", fontStyle: 'italic', fontSize: '24px' },
  formal: { fontFamily: "'Times New Roman', serif", fontWeight: 'bold', fontStyle: 'italic', fontSize: '22px' },
  modern: { fontFamily: "'Helvetica', sans-serif", fontWeight: 'bold', fontStyle: 'italic', fontSize: '22px' },
  classic: { fontFamily: "'Courier New', monospace", fontStyle: 'italic', fontSize: '22px' }
};

const AdminPOAReview = ({ isAdmin = true }) => {
  const [poaDocuments, setPOADocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedPOA, setSelectedPOA] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  useEffect(() => {
    loadPOADocuments();
  }, [filter]);

  const loadPOADocuments = async () => {
    try {
      const status = filter === 'all' ? null : filter;
      const response = await poaAPI.getAllPOA(status);
      setPOADocuments(response.data);
    } catch (error) {
      console.error('Failed to load POA documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (poaId, caseName) => {
    try {
      const response = await poaAPI.getPOAPdf(poaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Limited_POA_${caseName.replace(/\s+/g, '_')}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('POA downloaded successfully');
    } catch (error) {
      toast.error('Failed to download POA');
    }
  };

  const handlePrint = async (poaId, title = 'Document') => {
    try {
      const response = await poaAPI.getPOAPdf(poaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create hidden iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      printFrame.src = url;
      
      document.body.appendChild(printFrame);
      
      printFrame.onload = () => {
        setTimeout(() => {
          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
          } catch (e) {
            // Fallback: show preview modal with print instructions
            setPreviewUrl(url);
            setPreviewTitle(title);
            toast.info('Use the Download button to save and print.');
          }
          setTimeout(() => {
            document.body.removeChild(printFrame);
          }, 1000);
        }, 500);
      };
      
      toast.success('Preparing document for printing...');
    } catch (error) {
      toast.error('Failed to print POA');
    }
  };

  const handleView = async (poaId, title = 'Limited Power of Attorney') => {
    try {
      const response = await poaAPI.getPOAPdf(poaId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewTitle(title);
    } catch (error) {
      toast.error('Failed to view POA');
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewTitle('');
  };

  const handleOpenSignModal = (poa) => {
    setSelectedPOA(poa);
    setShowSignModal(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_client: { 
        label: 'Awaiting Client Signature', 
        class: 'bg-amber-100 text-amber-700',
        icon: Clock
      },
      pending_admin: { 
        label: 'Awaiting Your Signature', 
        class: 'bg-blue-100 text-blue-700',
        icon: PenTool
      },
      completed: { 
        label: 'Completed', 
        class: 'bg-green-100 text-green-700',
        icon: CheckCircle
      }
    };
    const badge = badges[status] || badges.pending_client;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.class}`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-600">
        Loading POA documents...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Limited Power of Attorney Documents</h2>
          <p className="text-sm text-slate-600">Review and counter-sign POA documents from clients</p>
        </div>
        
        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'pending_admin', 'completed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {f === 'all' ? 'All' : f === 'pending_admin' ? 'Pending Signature' : 'Completed'}
            </button>
          ))}
        </div>
      </div>

      {/* POA Documents List */}
      {poaDocuments.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <FileText className="mx-auto text-slate-400 mb-4" size={48} />
          <p className="text-slate-600">No POA documents found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {poaDocuments.map((poa) => (
            <div 
              key={poa.id} 
              className="border border-slate-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-start">
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Limited POA - {poa.case_name}</h3>
                    <p className="text-sm text-slate-600">
                      Client: {poa.form_data?.signer_name} ({poa.form_data?.signer_email})
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span>Created: {format(new Date(poa.created_at), 'MMM dd, yyyy')}</span>
                      {poa.completed_at && (
                        <span>Completed: {format(new Date(poa.completed_at), 'MMM dd, yyyy')}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {getStatusBadge(poa.status)}
                  
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleView(poa.id, `Limited POA - ${poa.merchant_legal_name || poa.case_name || 'Document'}`)}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      title="View PDF"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => handleDownload(poa.id, poa.merchant_legal_name || poa.case_name || 'Document')}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Download PDF"
                    >
                      <Download size={18} />
                    </button>
                    <button
                      onClick={() => handlePrint(poa.id, `Limited POA - ${poa.merchant_legal_name || poa.case_name || 'Document'}`)}
                      className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Print"
                    >
                      <Printer size={18} />
                    </button>
                    {poa.dropbox_url && (
                      <a
                        href={poa.dropbox_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View in Dropbox"
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                    {poa.status === 'pending_admin' && isAdmin && (
                      <button
                        onClick={() => handleOpenSignModal(poa)}
                        className="ml-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2"
                      >
                        <PenTool size={16} />
                        Counter-Sign
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* POA Details */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Merchant:</span>
                    <p className="font-medium">{poa.form_data?.merchant_legal_name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Processor:</span>
                    <p className="font-medium">{poa.form_data?.processor_name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">MID(s):</span>
                    <p className="font-medium">{poa.form_data?.mid_numbers || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Client Signed:</span>
                    <p className="font-medium">
                      {poa.client_signature 
                        ? format(new Date(poa.client_signature.signed_at), 'MMM dd, yyyy')
                        : 'Pending'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Admin Sign Modal */}
      {showSignModal && selectedPOA && (
        <AdminSignModal
          poa={selectedPOA}
          onClose={() => {
            setShowSignModal(false);
            setSelectedPOA(null);
          }}
          onSuccess={() => {
            setShowSignModal(false);
            setSelectedPOA(null);
            loadPOADocuments();
          }}
        />
      )}

      {/* PDF Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">{previewTitle}</h3>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  download={`${previewTitle.replace(/\s+/g, '_')}.pdf`}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
                >
                  <Download size={16} />
                  Download
                </a>
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors text-sm font-medium"
                >
                  <ExternalLink size={16} />
                  Open in New Tab
                </a>
                <button
                  onClick={closePreview}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 p-4 overflow-hidden">
              <object
                data={previewUrl}
                type="application/pdf"
                className="w-full h-full border border-slate-200 rounded-lg"
              >
                <div className="flex flex-col items-center justify-center h-full bg-slate-50 rounded-lg p-8">
                  <FileText className="text-slate-400 mb-4" size={64} />
                  <p className="text-slate-600 mb-4 text-center">
                    Your browser cannot display this PDF inline.
                  </p>
                  <div className="flex gap-3">
                    <a
                      href={previewUrl}
                      download={`${previewTitle.replace(/\s+/g, '_')}.pdf`}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Download size={18} />
                      Download PDF
                    </a>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                    >
                      <ExternalLink size={18} />
                      Open in New Tab
                    </a>
                  </div>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Sign Modal Component
const AdminSignModal = ({ poa, onClose, onSuccess }) => {
  const [signerName, setSignerName] = useState('Padraig Deighan');
  const [signerTitle, setSignerTitle] = useState('CCO');
  const [signatureText, setSignatureText] = useState('');
  const [signatureFont, setSignatureFont] = useState('cursive');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSign = async () => {
    if (!signatureText || !signerName || !signerTitle) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!agreed) {
      toast.error('Please agree to the terms before signing');
      return;
    }

    setLoading(true);
    try {
      await poaAPI.adminSignPOA({
        poa_id: poa.id,
        signature_text: signatureText,
        signature_font: signatureFont,
        signer_name: signerName,
        signer_title: signerTitle
      });
      
      toast.success('POA signed and completed successfully! Document backed up to Dropbox.');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign POA');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex justify-between items-center z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <PenTool className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Counter-Sign POA</h2>
              <p className="text-sm text-slate-600">Case: {poa.case_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Client Signature Info */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
              <CheckCircle size={18} />
              Client Signature Verified
            </h3>
            <div className="text-sm text-green-700">
              <p><strong>Signed by:</strong> {poa.client_signature?.signer_name}, {poa.client_signature?.signer_title}</p>
              <p><strong>Date:</strong> {poa.client_signature?.signed_at ? format(new Date(poa.client_signature.signed_at), 'MMMM dd, yyyy at h:mm a') : 'N/A'}</p>
              <p><strong>IP:</strong> {poa.client_signature?.ip_address}</p>
            </div>
          </div>

          {/* Admin Signer Info */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">ReserveRelease.AI Representative</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          {/* Signature Input */}
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Your Signature</h3>
            <input
              type="text"
              value={signatureText}
              onChange={(e) => setSignatureText(e.target.value)}
              placeholder="Type your signature here"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-xl"
            />
            
            {/* Font Selection */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Signature Style</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(SIGNATURE_FONTS).map(([fontId, style]) => (
                  <button
                    key={fontId}
                    type="button"
                    onClick={() => setSignatureFont(fontId)}
                    className={`px-4 py-2 border rounded-lg transition-all ${
                      signatureFont === fontId 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span style={style} className="text-slate-800">Aa</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Signature Preview */}
          {signatureText && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Preview</h3>
              <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
                <div 
                  style={{
                    ...SIGNATURE_FONTS[signatureFont],
                    color: '#00008B',
                    borderBottom: '2px solid #333',
                    paddingBottom: '10px',
                    display: 'inline-block',
                    minWidth: '200px'
                  }}
                >
                  {signatureText}
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  <p><strong>{signerName}</strong>, {signerTitle}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Date: {new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agreement Checkbox */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-slate-700">
                I confirm that I am an authorized representative of ReserveRelease.AI, LLC and I am authorized to counter-sign this Limited Power of Attorney on behalf of the company. 
                My electronic signature is legally binding.
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-6 flex gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSign}
            disabled={loading || !agreed || !signatureText}
            className="flex-1 primary-button py-3 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              'Signing & Uploading...'
            ) : (
              <>
                <CheckCircle size={20} />
                Sign & Complete POA
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPOAReview;
